import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { EnterpriseProgramMemberships_ProgramMembershipState as MembershipState } from '__generated__/graphql-types';

import type { NaptimeThirdPartyOrganizationFromSlugFragment } from 'bundles/enterprise-legacy-learner-home/queries/__generated__/EnterpriseProgramBySlugQuery';
import ProgramMembershipsByUserIdQuery from 'bundles/unified-home-common/queries/ProgramMembershipsByUserId.graphql';
import SelectPermissionsUnderDomain from 'bundles/unified-home-common/queries/SelectPermissionsUnderDomain.graphql';
import type {
  ProgramMembershipsQuery,
  ProgramMembershipsQueryVariables,
} from 'bundles/unified-home-common/queries/__generated__/ProgramMembershipsByUserId';
import type {
  SelectPermissionsUnderDomainQuery,
  SelectPermissionsUnderDomainQueryVariables,
} from 'bundles/unified-home-common/queries/__generated__/SelectPermissionsUnderDomain';

const useOrgScopedProgramMembership = (
  userId: number,
  thirdPartyOrganization?: NaptimeThirdPartyOrganizationFromSlugFragment
) => {
  const {
    data: programMembershipsData,
    loading: programMembershipsDataLoading,
    refetch,
  } = useQuery<ProgramMembershipsQuery, ProgramMembershipsQueryVariables>(ProgramMembershipsByUserIdQuery, {
    variables: { userId },
    skip: !userId,
  });
  const { data: selectPermissionsUnderDomainData, loading: selectPermissionsUnderDomainDataLoading } = useQuery<
    SelectPermissionsUnderDomainQuery,
    SelectPermissionsUnderDomainQueryVariables
  >(SelectPermissionsUnderDomain, {
    variables: {
      thirdPartyOrganizationSlug: thirdPartyOrganization?.slug || '',
      permissions: [{ domain: 'PERMISSION_DOMAIN_PROGRAM', verb: 'VIEW' }],
      userId,
    },
    skip: !thirdPartyOrganization?.id || !userId,
  });

  const isAdminOfAnyProgramInOrg = useMemo(
    () =>
      selectPermissionsUnderDomainData?.EnterprisePermission?.querySelectPermissionsUnderDomain?.some(
        (selectPermissionsUnderDomain) => selectPermissionsUnderDomain.permissions.length > 0
      ),
    [selectPermissionsUnderDomainData]
  );

  const orgScopedProgramMemberships = useMemo(
    () =>
      programMembershipsData?.ProgramMemberships?.queryProgramMembershipsByUserIdAndIncludeInvitations?.edges?.filter(
        (edge) => edge?.node?.program?.naptimeThirdPartyOrganization?.id === thirdPartyOrganization?.id
      ),
    [programMembershipsData, thirdPartyOrganization?.id]
  );

  const isMemberOfAnyProgramInOrg = orgScopedProgramMemberships?.some(
    (edge) => edge?.node?.state === MembershipState.ProgramMembershipStateMember
  );

  const isInvitedToAnyProgramInOrg = orgScopedProgramMemberships?.some(
    (edge) => edge?.node?.state === MembershipState.ProgramMembershipStateInvited
  );

  const isAllowlistedToAnyProgramInOrg = orgScopedProgramMemberships?.some(
    (edge) => edge?.node?.state === MembershipState.ProgramMembershipStateAllowlisted
  );

  const isEmailVerified = orgScopedProgramMemberships?.some((edge) => !!edge?.node?.emailVerified);

  const shouldShowEmailVerification =
    !isEmailVerified && (!!isInvitedToAnyProgramInOrg || !!isAllowlistedToAnyProgramInOrg);

  return {
    isMemberOfAnyProgramInOrg,
    isAdminOfAnyProgramInOrg,
    isEmailVerified,
    isInvitedToAnyProgramInOrg,
    isAllowlistedToAnyProgramInOrg,
    shouldShowEmailVerification,
    refetch,
    isLoading: programMembershipsDataLoading || selectPermissionsUnderDomainDataLoading,
  };
};

export default useOrgScopedProgramMembership;
